import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import { Tooltip } from "@msuite/ui/tooltip";
import type { applyOffsets } from "@msuite/utils/incoming-invoices";
import { formatInvoiceAmount } from "@/components/incoming-invoices";

/** Props Interface */
interface IncomingInvoiceOffsetAmountProps {
	outgoingInvoiceId: string;
	/** The plan applied to today's payment. */
	result: ReturnType<typeof applyOffsets>;
	currency: string;
}

/**
 * What one planned outgoing invoice takes off the payment, or that the payment is covered before
 * it is reached — a plan the export refuses until the invoice is taken out of it.
 */
export function IncomingInvoiceOffsetAmount({
	outgoingInvoiceId,
	result,
	currency,
}: IncomingInvoiceOffsetAmountProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	let amount: number | null = null;
	for (const offset of result.applied) {
		if (offset.id === outgoingInvoiceId) amount = offset.amount;
	}

	/** Guard */
	if (amount === null) {
		return (
			<Tooltip label={t("incoming_invoices.payments.offsets.not_reached_hint")}>
				<Tag
					size="xs"
					color="amber"
					variant="plain"
				>
					{t("incoming_invoices.payments.offsets.not_reached")}
				</Tag>
			</Tooltip>
		);
	}

	/** Render */
	return <span className="tabular-nums">{formatInvoiceAmount(amount, currency)}</span>;
}
